import axios from 'axios';
import { isTokenExpired } from '../auth/tokenUtils';

/** Stay under nginx default client_max_body_size (1m) with JSON overhead. */
export const PRESIGNED_URL_MAX_REQUEST_BYTES = 512 * 1024;
/** Cap presign batch size for backend latency (whichever limit hits first). */
export const PRESIGNED_URL_MAX_COUNT = 200;
export const ETL_RUNS_PAGE_SIZE = 100;

export interface PresignedUrlItem {
  s3_url: string;
  presigned_url?: string;
  error?: string;
}

export interface ETLRunRow {
  id?: number;
  run_name: string;
  robot_name: string;
  start_time: string | null;
  end_time?: string | null;
  status: string;
  created_at?: string;
  cleaning_status?: string;
  needs_attention?: boolean;
  error_message?: string;
  retry_count?: number;
  failure_class?: string;
  task_memory?: string;
  stop_reason?: string;
}

export interface ETLRunsPage {
  total: number;
  runs: ETLRunRow[];
}

function presignRequestBodyBytes(urls: string[]): number {
  return new TextEncoder().encode(JSON.stringify({ s3_urls: urls })).length;
}

/** Split S3 URL lists so each POST body stays under proxy/backend size limits. */
export function chunkPresignedUrlRequests(urls: string[]): string[][] {
  if (urls.length === 0) return [];

  const chunks: string[][] = [];
  let current: string[] = [];

  for (const url of urls) {
    const candidate = [...current, url];
    const overBytes =
      current.length > 0 && presignRequestBodyBytes(candidate) > PRESIGNED_URL_MAX_REQUEST_BYTES;
    const overCount = current.length >= PRESIGNED_URL_MAX_COUNT;

    if (overBytes || overCount) {
      chunks.push(current);
      current = [url];
    } else {
      current = candidate;
    }
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

// Determine API base URL based on environment
// TODO: After containerizing, change to API gateway
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is explicitly set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In development mode (npm start), connect directly to backend
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api';
  }
  
  // In production (Docker), use relative path which nginx will proxy
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Picsellia routes are served by the DataViewer backend under /api/picsellia
const getPicselliaApiBaseUrl = () => {
  if (process.env.REACT_APP_PICSELLIA_API_URL) {
    return process.env.REACT_APP_PICSELLIA_API_URL;
  }
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api/picsellia';
  }
  return '/api/picsellia';
};

const PICSELLIA_API_BASE_URL = getPicselliaApiBaseUrl();

let authTokenProvider: (() => string | null) | null = null;
let authRefreshHandler: (() => Promise<string | null>) | null = null;

export function setAuthTokenProvider(provider: () => string | null): void {
  authTokenProvider = provider;
}

export function setAuthRefreshHandler(handler: (() => Promise<string | null>) | null): void {
  authRefreshHandler = handler;
}

type RetriableRequestConfig = {
  _authRetry?: boolean;
  headers?: Record<string, unknown>;
  url?: string;
  baseURL?: string;
};

// Log the API base URL in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('[API Client] Using base URL:', API_BASE_URL);
  console.log('[API Client] Make sure backend is running on http://localhost:8000');
  console.log('[API Client] Picsellia API base URL:', PICSELLIA_API_BASE_URL);
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach Google ID token and handle FormData uploads
apiClient.interceptors.request.use(async (config) => {
  let token = authTokenProvider?.() ?? null;
  if (authRefreshHandler && token && isTokenExpired(token, 60_000)) {
    const refreshed = await authRefreshHandler();
    if (refreshed) {
      token = refreshed;
    }
  }
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    // Remove Content-Type header to let browser set it automatically with boundary
    if (config.headers) {
      delete (config.headers as any)['Content-Type'];
    }
  }
  return config;
});

// On 401, silently refresh the Google ID token once and retry (avoids mid-session logout).
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as RetriableRequestConfig | undefined;
    if (error.response?.status === 401 && config && !config._authRetry && authRefreshHandler) {
      config._authRetry = true;
      const newToken = await authRefreshHandler();
      if (newToken) {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${newToken}` };
        if (/^https?:/i.test(config.url || '')) {
          config.baseURL = '';
        }
        return apiClient.request(config as Parameters<typeof apiClient.request>[0]);
      }
    }
    return Promise.reject(error);
  },
);

async function fetchPresignedUrlsChunked(
  s3Urls: string[]
): Promise<{ data: { urls: PresignedUrlItem[] } }> {
  if (s3Urls.length === 0) {
    return { data: { urls: [] } };
  }
  const chunks = chunkPresignedUrlRequests(s3Urls);
  const allUrls: PresignedUrlItem[] = [];
  for (const chunk of chunks) {
    const response = await apiClient.post<{ urls: PresignedUrlItem[] }>('/presigned-urls', {
      s3_urls: chunk,
    });
    allUrls.push(...response.data.urls);
  }
  return { data: { urls: allUrls } };
}

export interface CameraInfo {
  index: number;
  mxid: string;
}

export interface GroundTruthLabel {
  class_name: string;
  confidence?: number;
  bbox?: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  is_ground_truth?: boolean;
  source?: string;
  class_id?: number;
}

export interface BatchProgress {
  batch_id: string;
  status: string;
  total_images: number;
  downloaded: number;
  inference_completed: number;
  failed_downloads: number;
  failed_inferences: number;
  download_percentage: number;
  inference_percentage: number;
  elapsed_time: number;
  current_image: string;
  results: any[];
}

export interface NeraAssessment {
  id: number;
  fish_id: string | null;
  collection_date: string | null;
  right_url: string | null;
  left_url: string | null;
  bottom_url: string | null;
  robot_name: string | null;
}

export type NeraAssessmentRef = {
  id: number;
  collection_date: string | null;
};

export type SensoryScoreMap = Record<string, number>;

export type HsiCapture = Record<string, unknown> & {
  capture_id: string;
  experiment_id?: string;
  fish_id?: string;
  nera_assessment_ids?: number[];
  nera_assessments?: NeraAssessmentRef[];
  species?: string;
  camera_type?: string;
  fish_part?: string;
  days_since_kill?: number;
  s3_uri?: string;
  ingest_status?: string;
  ingested_at?: string;
  taste_score?: number | SensoryScoreMap;
  texture?: string;
  odor?: string;
  tags?: string[];
};

export type TpaSample = {
  t: number;
  gf: number;
};

export type TpaRun = {
  pk: string;
  sk: string;
  run_id: string;
  experiment_id?: string;
  group_id?: string;
  fish_id?: string;
  section?: string;
  days_since_kill?: number;
  frozen_state?: string;
  saved_at?: string;
  s3_uri?: string;
  label?: string;
  species?: string;
  test_type?: string;
  method_code?: string;
  orientation?: string;
  surface?: string;
  peak_force_gf?: number;
  peak_force_n?: number;
  peak_force_kgf?: number;
  peak_t_s?: number;
  duration_s?: number;
  n_samples?: number;
  schema_version?: number;
  entity?: string;
  samples?: TpaSample[];
  curve_error?: string;
};

export const api = {
  // DataViewer Backend endpoints
  dataviewer: {
    query: (data: { robot_name: string; start_date: string; end_date: string; query_template?: string }) =>
      apiClient.post<{ job_id: string; status: string; message: string }>('/query', data),
    getJobStatus: (jobId: string) =>
      apiClient.get<{ job_id: string; status: string; message: string; file_path?: string; error?: string }>(`/job/${jobId}`),
    getJobResult: (jobId: string) =>
      apiClient.get<{ success: boolean; message: string }>(`/job/${jobId}/result`),
    getJobResults: (jobId: string) =>
      apiClient.get<Array<{ [s3_url: string]: string[] | { tags?: string[]; species?: string | null } }>>(`/job/${jobId}/results`),
    getQueryImageData: (fileLocation: string) =>
      apiClient.get<{ image_url: string; ground_truth_labels?: GroundTruthLabel[]; metadata?: any }>(
        '/query/image-data',
        { params: { file_location: fileLocation } }
      ),
    getPresignedUrls: (s3Urls: string[]) => fetchPresignedUrlsChunked(s3Urls),
    getTagsBatch: (image_urls: string[]) =>
      apiClient.post<{ results: Record<string, string[]> }>('/tags-batch', { image_urls }),
    getMetadataBatch: (image_urls: string[]) =>
      apiClient.post<{
        results: Record<string, { tags: string[]; species: string | null; metrics?: Record<string, unknown> }>;
      }>('/metadata-batch', { image_urls }),
    addTags: (data: { image_urls: string[]; tags: string[] }) =>
      apiClient.post<{ total_records: number; successful_updates: number; errors: string[] }>('/add-tags', data),
    removeTag: (data: { image_urls: string[]; tag: string }) =>
      apiClient.post<{ total_records: number; successful_updates: number; errors: string[] }>('/remove-tag', data),
    setSpecies: (data: { image_urls: string[]; species: string }) =>
      apiClient.post<{ total_records: number; successful_updates: number; errors: string[] }>('/set-species', data),
    clearSpecies: (data: { image_urls: string[] }) =>
      apiClient.post<{ total_records: number; successful_updates: number; errors: string[] }>('/clear-species', data),
    setImageDims: (data: {
      updates: Array<{ file_location: string; img_width: number; img_height: number }>;
    }) =>
      apiClient.post<{ total_records: number; successful_updates: number; errors: string[] }>(
        '/set-image-dims',
        data
      ),
    addClassifications: (data: { image_url?: string; image_urls?: string[]; classifications: string[] }) =>
      apiClient.post<{ success?: boolean; message?: string; error?: string; successful_updates?: number; total_records?: number; total_requested?: number; updated_count?: number; errors?: string[] }>('/add-classifications', data),
    removeClassification: (data: { image_urls: string[]; classification: string }) =>
      apiClient.post<{ total_records: number; successful_updates: number; errors: string[] }>('/remove-classification', data),
    getClassificationsBatch: (data: {
      image_urls: string[];
      dvc_dataset_name?: string;
      dvc_dataset_version?: string;
    }) =>
      apiClient.post<{ results: Record<string, string[]> }>('/classifications-batch', data),
    getETLRuns: (params?: {
      run_name?: string;
      start_date?: string;
      end_date?: string;
      status?: string;
      limit?: number;
      offset?: number;
      sort_by?: 'start_time' | 'created_at';
    }) =>
      apiClient.get<ETLRunsPage>('/etl-runs', { params }),
    etlRunLabels: (runId: number) =>
      apiClient.get<{ labels: Array<{ name: string; kind: 'box' | 'image' }> }>(`/etl-runs/${runId}/labels`),
    listMcapRobots: () => apiClient.get<{ robots: string[] }>('/mcaps/catalog/robots'),
    listMcapCatalog: (robot: string) =>
      apiClient.get<{
        robot: string;
        mcaps: Array<{
          s3_uri: string;
          key: string;
          size_bytes?: number | null;
          fish_species: string[];
          fish_states_seen: string[];
          run_modes_seen: string[];
          calibration_was_run: boolean;
          camera_error_seen: boolean;
          cv_failure_seen: boolean;
          proportional_default_seen: boolean;
          proportional_default_packet_count: number;
        }>;
      }>(`/mcaps/catalog/${encodeURIComponent(robot)}`),
    fetchAllETLRuns: async (params?: {
      run_name?: string;
      start_date?: string;
      end_date?: string;
      sort_by?: 'start_time' | 'created_at';
    }) => {
      const runs: ETLRunRow[] = [];
      let offset = 0;
      let total = 0;
      do {
        const res = await apiClient.get<ETLRunsPage>('/etl-runs', {
          params: {
            ...params,
            limit: ETL_RUNS_PAGE_SIZE,
            offset,
            sort_by: params?.sort_by ?? 'start_time',
          },
        });
        total = res.data.total;
        runs.push(...res.data.runs);
        if (res.data.runs.length === 0) {
          break;
        }
        offset += ETL_RUNS_PAGE_SIZE;
      } while (runs.length < total);
      return { data: { runs, total } };
    },
    getETLSummary: () => apiClient.get<{ by_status: Array<{ status: string; n: number }> }>('/etl-runs/summary'),
    getNominalAssets: (search?: string) =>
      apiClient.get<{ assets: Array<{ rid: string; name: string }> }>('/nominal/assets', {
        params: search ? { search } : undefined,
      }),
    patchCleaning: (runId: number, body: { cleaning_status?: string; needs_attention?: boolean }) =>
      apiClient.patch(`/etl-runs/${runId}/cleaning`, body),
    getQueryCounts: (params?: Record<string, string>) => apiClient.get('/query/counts', { params }),
    getQueryRobots: () => apiClient.get<{ robots: string[] }>('/query/robots'),
    getQueryImages: (params?: Record<string, string | number>) => apiClient.get('/query/images', { params }),
    getQueryOptions: () =>
      apiClient.get<{
        tags: string[];
        columns: Record<string, string[]>;
        bbox_labels: string[];
        classification_labels: string[];
        fields: Array<{ name: string; kind: string; examples: string[] }>;
      }>('/query/options'),
    searchQuery: (body: { where: string; columns: string[] }) =>
      apiClient.post<{ columns: string[]; rows: Array<Record<string, unknown>>; limited: boolean }>(
        '/query/search',
        body,
      ),
    listFormTemplates: (kind: string) => apiClient.get<{ names: string[] }>(`/form-templates/${kind}`),
    getFormTemplate: (kind: string, name: string) => apiClient.get(`/form-templates/${kind}/${encodeURIComponent(name)}`),
    saveFormTemplate: (kind: string, name: string, spec: Record<string, unknown>) =>
      apiClient.put<{ uri: string }>(`/form-templates/${kind}/${encodeURIComponent(name)}`, spec),
    formSuggestions: (kind: string) =>
      apiClient.get<{ by_path: Record<string, string[]> }>(`/form-suggestions/${kind}`),
    listRegistryDatasets: () => apiClient.get<{ datasets: Array<{ dataset_id: string; name: string; version: string }> }>('/datasets/registry'),
    listProductionDatasets: () => apiClient.get<{ datasets: Array<{ dataset_id: string; name: string; version: string }> }>('/datasets/production-registry'),
    listRegistryImages: (name: string, version: string) =>
      apiClient.get<{ images: string[] }>(
        `/datasets/registry/${encodeURIComponent(name)}/${encodeURIComponent(version)}/images`,
      ),
    datasetSummary: (name: string, version: string) =>
      apiClient.get<{
        name: string;
        version: string;
        rows: number;
        images: number;
        classes: Array<{ name: string; boxes: number; rows: number }>;
      }>(`/datasets/registry/${encodeURIComponent(name)}/${encodeURIComponent(version)}/summary`),
    dryRunDataset: (spec: Record<string, unknown>) => apiClient.post('/datasets/dry-run', spec),
    fillRandomBoxes: (body: { csv_text: string; source_dataset: string }) =>
      apiClient.post('/datasets/synthetic-bboxes', body),
    acceptDataset: (body: { spec: Record<string, unknown>; csv_text: string }) => apiClient.post('/datasets/accept', body),
    createNominalRun: (body: Record<string, unknown>) => apiClient.post('/etl-runs/nominal', body),
    getManualBacklog: () =>
      apiClient.get<{ enabled: boolean; runs: ETLRunRow[] }>('/etl/manual-backlog'),
    ingestBacklogRun: (runId: number) =>
      apiClient.post<{ run_id: number; task_arn?: string }>(`/etl-runs/${runId}/ingest`),
    deleteBacklogRun: (runId: number) => apiClient.delete(`/etl-runs/${runId}`),
    requeueBacklogRuns: (runIds: number[]) =>
      apiClient.post<{ ok: boolean; ids: number[] }>('/etl-runs/requeue', { run_ids: runIds }),
    refreshNominalBacklog: () =>
      apiClient.post<{ added: number; fetched: number }>('/etl/manual-backlog/refresh', null, { timeout: 180000 }),
    resetCompletedRun: (runId: number) =>
      apiClient.post<{ ok: boolean; id: number; deleted_rows: number }>(`/etl-runs/${runId}/reset`),
    neraBackfill: (params: Record<string, string>) => apiClient.post('/nera/backfill', null, { params }),
    listNeraJobs: () =>
      apiClient.get<{
        jobs: Array<{
          job_id: string;
          start_date: string;
          end_date: string;
          machine?: string | null;
          status: string;
          error_message?: string | null;
          ecs_task_arn?: string | null;
          created_at?: string | null;
          updated_at?: string | null;
        }>;
      }>('/nera/jobs'),
    listJobs: (kind?: string) => apiClient.get('/jobs', { params: kind ? { kind } : {} }),
    getJob: (id: string) => apiClient.get(`/jobs/${id}`),
    submitJob: (kind: string, spec: Record<string, unknown>, name?: string) =>
      apiClient.post(`/jobs/${kind}`, { spec, name }),
    artifactUrl: (jobId: string, artifactId: string) =>
      apiClient.get<{ url: string }>(`/jobs/${jobId}/artifacts/${artifactId}/url`),
    listInferenceModels: () =>
      apiClient.get<{ models: Array<{ s3_uri: string; key: string }> }>('/inference/models'),
    inferImage: (body: { model_s3_uri: string; image_url: string; confidence_threshold?: number }) =>
      apiClient.post<{
        image_url: string;
        model_s3_uri: string;
        device: string;
        success: boolean;
        predictions: Array<{
          class_name?: string;
          confidence?: number;
          bbox?: { x1: number; y1: number; x2: number; y2: number } | null;
        }>;
        error?: string;
      }>('/inference/predict', body),
    modelRegistry: () => apiClient.get('/model-registry'),
    inspectModel: (body: { model_name: string; version: string }) =>
      apiClient.post<{
        model_name: string;
        version: string;
        check_status: string;
        model_classes: Record<string, string>;
        expected_classes: Record<string, string | null>;
        issues: string[];
        num_classes: number;
      }>('/model-registry/inspect', body),
    promoteModel: (body: { model_name: string; version: string; stage: 'Checked' | 'Not Convertable' }) =>
      apiClient.post('/model-registry/promote', body),
    syncRegistry: () => apiClient.post('/model-registry/sync'),
    requestAlias: (body: Record<string, unknown>) => apiClient.post('/model-registry/alias-requests', body),
    reviewAlias: (id: number, body: { decision: string; comment?: string }) =>
      apiClient.post(`/model-registry/alias-requests/${id}/review`, body),
    getQueryTemplates: () =>
      apiClient.get<{ [key: string]: { name: string; description: string } }>('/query-templates'),
  },

  // Picsellia endpoints
  picsellia: {
    sendToPicsellia: (data: { s3_links: string[]; datalake_name?: string }) =>
      apiClient.post<{
        job_id: string;
        status: string;
        message: string;
        image_count?: number;
      }>('/send_to_picsellia', data, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    getUploadJobStatus: (jobId: string) =>
      apiClient.get<{
        job_id: string;
        status: string;
        message: string;
        success?: boolean;
        uploaded_count?: number;
        skipped_already_in_picsellia?: number;
        updated?: number;
        skipped?: number;
        not_found?: number;
        record_count?: number;
        completed?: number;
        total?: number | null;
        error?: string;
      }>(`/upload-job/${jobId}`, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    importAnnotations: (data: {
      dataset_name: string;
      version: string;
      annotation_file_path: string;
      annotation_format?: 'coco' | 'voc';
      force_create_label?: boolean;
      mode?: 'REPLACE' | 'CONCATENATE' | 'SKIP';
    }) =>
      apiClient.post<{ success: boolean; message?: string }>('/import_annotations', data, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    getDatasets: () =>
      apiClient.get<{ datasets: string[] }>('/datasets', {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    listDatasetVersions: (datasetName: string) =>
      apiClient.get<{ dataset_name: string; versions: string[] }>(`/datasets/${datasetName}/versions`, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    getS3UrlsForDatasetVersion: (datasetName: string, version: string) =>
      apiClient.get<{ dataset_name: string; version: string; s3_urls: string[]; count: number }>(
        `/datasets/${encodeURIComponent(datasetName)}/versions/${encodeURIComponent(version)}/s3_urls`,
        { baseURL: PICSELLIA_API_BASE_URL }
      ),
    ingestDatasetVersion: (data: { dataset_name: string; version: string }) =>
      apiClient.post<{
        job_id: string;
        status: string;
        message: string;
        dataset_name?: string;
        version?: string;
      }>('/datasets/ingest', data, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    updatePicselliaTags: (data: {
      dataset_name: string;
      version: string;
      datalake_name?: string;
    }) =>
      apiClient.post<{
        job_id: string;
        status: string;
        message: string;
        dataset_name?: string;
        version?: string;
      }>('/update_picsellia_tags', data, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    syncPostgresTagsFromPicsellia: (data: { dataset_name: string; version: string }) =>
      apiClient.post<{
        job_id: string;
        status: string;
        message: string;
        dataset_name?: string;
        version?: string;
      }>('/sync_postgres_tags_from_picsellia', data, {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
    healthCheck: () =>
      apiClient.get<{ status: string }>('/health', {
        baseURL: PICSELLIA_API_BASE_URL,
      }),
  },
  dvcviewer: {
    getDatasets: () =>
      apiClient.get<{ datasets: string[] }>('/dvc/datasets'),
    getVersions: (datasetName: string) =>
      apiClient.get<{ versions: string[] }>(`/dvc/datasets/${datasetName}/versions`),
    getSampleData: (datasetName: string, version: string) =>
      apiClient.get<{
        sample_urls: string[];
        total_images: number;
        metadata: any;
        dataset_info: any;
        columns: string[];
        num_records: number;
      }>(`/dvc/datasets/${datasetName}/${version}/sample-data`),
    getClassificationIndex: (datasetName: string, version: string) =>
      apiClient.get<{
        results_csv: Record<string, string[]>;
        results_db: Record<string, string[]>;
        results_merged: Record<string, string[]>;
        all_classification_names: string[];
        num_urls: number;
      }>(`/dvc/datasets/${encodeURIComponent(datasetName)}/${encodeURIComponent(version)}/classification-index`),
    getWeightIndex: (datasetName: string, version: string) =>
      apiClient.get<{
        results: Record<string, number | null>;
        num_with_weight: number;
        num_urls: number;
      }>(`/dvc/datasets/${encodeURIComponent(datasetName)}/${encodeURIComponent(version)}/weight-index`),
    getBboxIndex: (datasetName: string, version: string) =>
      apiClient.get<{
        results: Record<
          string,
          Array<{
            class_name: string;
            bbox: { x1: number; y1: number; x2: number; y2: number };
            type?: string;
          }>
        >;
        num_with_bboxes: number;
        num_urls: number;
      }>(`/dvc/datasets/${encodeURIComponent(datasetName)}/${encodeURIComponent(version)}/bbox-index`),
    getImageData: (datasetName: string, version: string, imageUrl: string) =>
      apiClient.get<{
        image_url: string;
        ground_truth_labels?: GroundTruthLabel[];
        keypoints?: Array<{ category: string; x: number; y: number }>;
        metadata?: any;
      }>(`/dvc/datasets/${datasetName}/${version}/image-data`, {
        params: { image_url: imageUrl },
      }),
    getPresignedUrl: (s3Url: string) =>
      apiClient.get<{ presigned_url: string }>(`/dvc/image/presigned-url`, {
        params: { s3_url: s3Url },
      }),
    startBatch: (data: { dataset_name: string; version: string; image_urls: string[] }) =>
      apiClient.post<{ batch_id: string; status: string }>('/dvc/batch/start', data),
    getBatchProgress: (batchId: string) =>
      apiClient.get<BatchProgress>(`/dvc/batch/progress/${batchId}`),
    stopBatch: () =>
      apiClient.post<{ status: string }>('/dvc/batch/stop'),
  },

  nera: {
    getAssessments: (params: { date: string; robot_name?: string; limit?: number }) =>
      apiClient.get<{ assessments: NeraAssessment[] }>('/nera/assessments', { params }),
    getDatesWithData: (params?: { month?: string }) =>
      apiClient.get<{ dates: string[] }>('/nera/dates-with-data', { params }),
    getRobots: () =>
      apiClient.get<{ robots: string[] }>('/nera/robots'),
  },
  hsi: {
    listCaptures: (params?: {
      experiment_id?: string;
      species?: string;
    }) =>
      apiClient.get<{ captures: HsiCapture[]; total: number }>('/hsi/captures', { params }),
    getCapture: (captureId: string) =>
      apiClient.get<HsiCapture>(`/hsi/captures/${encodeURIComponent(captureId)}`),
    patchCapture: (captureId: string, attributes: Record<string, unknown>) =>
      apiClient.patch<HsiCapture>(
        `/hsi/captures/${encodeURIComponent(captureId)}`,
        { attributes }
      ),
    getPreview: (captureId: string, view: 'rgb' | 'rpca' = 'rgb') =>
      apiClient.get(`/hsi/captures/${encodeURIComponent(captureId)}/preview`, {
        params: { view },
        responseType: view === 'rgb' ? 'blob' : 'json',
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 501,
      }),

    patchSensory: (
      captureId: string,
      body: { inputter: string; taste_score?: number}
    ) =>
      apiClient.patch<{
        experiment_id: string;
        fish_id: string;
        inputter: string;
        taste_score: SensoryScoreMap;
        taste_avg: number | null;
        updated_captures: number;
      }>(`/hsi/captures/${encodeURIComponent(captureId)}/sensory`, body),

      addTags: (data: { capture_ids: string[]; tags: string[] }) =>
        apiClient.post<{
          total_records: number;
          successful_updates: number;
          errors: string[];
        }>('/hsi/add-tags', data),
        
      removeTag: (data: { capture_ids: string[]; tag: string }) =>
        apiClient.post<{
          total_records: number;
          successful_updates: number;
          errors: string[];
        }>('/hsi/remove-tag', data),
  },

  tpa: {
    listRuns: (params?: {
      experiment_id?: string;
      group_id?: string;
      fish_id?: string;
      section?: string;
      surface?: string;
      species?: string;
      frozen_state?: string;
      test_type?: string;
    }) =>
      apiClient.get<{ runs: TpaRun[]; total: number }>('/tpa/runs', { params }),
    getRun: (runId: string) =>
      apiClient.get<TpaRun>(`/tpa/runs/${encodeURIComponent(runId)}`),
    getCurve: (runId: string) =>
      apiClient.get<TpaRun>(`/tpa/runs/${encodeURIComponent(runId)}/curve`),
    getSeries: (params: {
      experiment_id?: string;
      group_id?: string;
      fish_id: string;
      section: string;
      surface?: string;
    }) =>
      apiClient.get<{
        experiment_id?: string | null;
        group_id?: string | null;
        fish_id: string;
        section: string;
        surface?: string | null;
        runs: TpaRun[];
        total: number;
      }>('/tpa/series', { params }),
  },

  autolabel: {
    getModels: (ontologyType?: string) =>
      apiClient.get<{ models: Array<{
        model_id: number;
        architecture: string;
        mlflow_uri: string;
        ontology_type: string;
        purpose: string;
        classes: any;
        model_created_at: string;
      }> }>('/models', { params: ontologyType ? { ontology_type: ontologyType } : {} }),
  
    runAutolabel: (data: { model_id: number; image_urls: string[] }) =>
      apiClient.post<{ job_id: string; status: string; message: string; image_count: number }>(
        '/autolabel/run', data
      ),
  
    getAutolabelJobStatus: (jobId: string) =>
      apiClient.get<{ job_id: string; status: string; message: string; error?: string }>(
        `/autolabel/job/${jobId}`
      ),
  
    getPredictions: (datapointId: number) =>
      apiClient.get<{ predictions: Array<{
        prediction_id: number;
        model_id: number;
        confidence: number | null;
        ontology_type: string;
        ontology_purpose: string;
        architecture: string;
        created_at: string;
        label: { label_id: number; active: boolean; source: string } | null;
        bbox: { category: string; x: number; y: number; width: number; height: number } | null;
        classification: string[] | null;
        keypoint: { category: string; x: number; y: number } | null;
      }> }>('/predictions', { params: { datapoint_id: datapointId } }),
  
    getPredictionsBatch: (imageUrls: string[]) =>
      apiClient.post<{ results: Record<string, { total: number; accepted: number; rejected: number; pending: number }> }>(
        '/predictions-batch', { image_urls: imageUrls }
      ),
  
    acceptLabels: (predictionIds: number[]) =>
      apiClient.post('/labels/accept', { prediction_ids: predictionIds }),
  
    rejectLabels: (predictionIds: number[]) =>
      apiClient.post('/labels/reject', { prediction_ids: predictionIds }),

    listConversions: (target: string) =>
      apiClient.get('/model-registry/conversions', { params: { target } }),
    startConversion: (body: { model_version_id: number; target: string; gpu_identity: string }) =>
      apiClient.post('/model-registry/conversions', body),
    submitJob: (body: { template_name: string; etl_run_id: number; conversion_id: number; name?: string }) =>
      apiClient.post('/autolabel/submit', body),
    proposals: (jobId: string) => apiClient.get(`/autolabel/jobs/${jobId}/proposals`),
    reviewProposal: (id: number, body: { decision: string; payload?: Record<string, unknown> }) =>
      apiClient.post(`/autolabel/proposals/${id}/review`, body),
    sendRejected: (jobId: string) => apiClient.post(`/autolabel/jobs/${jobId}/rejected/picsellia`),
  },

};

export default api;
